import { Injector, Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { SentryErrorHandler, captureException, getGlobalScope, withScope } from '@sentry/angular';
import { ErrorResponse, JsonApiError } from '../airbrush/base';
import { RegionService } from '../region.service';
import { ENVIRONMENT_CONFIG } from '@parashift/shared/environment-config';

@Injectable()
export class GlobalErrorHandler extends SentryErrorHandler {
  private injector = inject<Injector>(Injector);
  private environment = inject(ENVIRONMENT_CONFIG);

  constructor() {
    super({ logErrors: true });
  }

  private get regionService(): RegionService {
    return this.injector.get(RegionService);
  }

  handleError(error: any) {
    if (this.environment.enable_sentry) {
      if (error?.status === 401 || error?.status === 'unauthorized') {
        return;
      }

      const region = this.regionService.region as string;
      const environment = region ? region.toLowerCase() + '-' + this.environment.env : this.environment.env;

      getGlobalScope().setTag('environment', environment);
      console.error(error);

      if (error instanceof ErrorResponse) {
        withScope(scope => {
          scope.setTag('region', this.regionService.region as string);
          scope.setExtras(this.getJsonApiErrorData(error));
          captureException(`JSONAPI Error ${error.status}`);
        });

      } else {
        withScope(scope => {
          scope.setTag('region', this.regionService.region as string);
          captureException(this.extractError(error));
        });
      }
    } else {
      throw error;
    }
  }

  private getJsonApiErrorData(error: ErrorResponse): { request_url: string, json_api_errors: JsonApiError[]} {
    return { request_url: error?.url, json_api_errors: error?.errors };
  }

  private extractError(errorCandidate: unknown): unknown {
    let error = errorCandidate;

    if (error && (error as { ngOriginalError: Error }).ngOriginalError) {
      error = (error as { ngOriginalError: Error }).ngOriginalError;
    }

    if (typeof error === 'string' || error instanceof Error) {
      return error;
    }

    if (error instanceof HttpErrorResponse) {
      if (error.error instanceof Error) {
        return error.error;
      }

      if (error.error instanceof ErrorEvent) {
        return error.error.message;
      }

      if (typeof error.error === 'string') {
        return `Server returned code ${error.status} with body "${error.error}"`;
      }

      return error.message;
    }

    return 'Handled unknown error';
  }
}
